// ============================================================================
// OfferTemplateButton — «Применение шаблона отклика» у текстового поля формы
// (вертикаль «Маркетплейс»: поле «Текст отклика» документа mkt_responses).
// Кнопка у поля → окно: список АКТИВНЫХ шаблонов (справочник ведёт оператор;
// мастеру — чтение), предпросмотр с ПОДСТАВЛЕННЫМИ переменными {{Поле}} из
// связанной заявки, вставка в поле («Вставить в текст» — добавить, «Заменить
// текст» — заменить). SSoT данных — сервер (см. lib/offerTemplates.ts).
// ============================================================================

import { useState } from 'react';
import { FileText } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { UniversalWindow } from '@/components/ui/UniversalWindow';
import { OfflineDataBanner } from '@/components/ui/OfflineDataBanner';
import { friendlyError, logErrorToJournal } from '@/lib/errors';
import {
  type OfferTemplate,
  type OfferTemplateContext,
  type OfferTemplateSettings,
  DEFAULT_OFFER_TEMPLATE_SETTINGS,
  EMPTY_OFFER_TEMPLATE_CONTEXT,
  applyOfferTemplate,
  loadActiveOfferTemplates,
  loadOfferTemplateContext,
} from '@/lib/offerTemplates';

interface Props {
  /** Текущее значение поля («Текст отклика»). */
  value: string;
  /** Применить готовый текст (значение поля целиком — решает вызывающий). */
  onApply: (text: string) => void;
  disabled?: boolean;
  /** Значения формы (нужны поле-ссылка на заявку — settings.requestField). */
  formValues?: Record<string, unknown>;
  settings?: OfferTemplateSettings;
}

export function OfferTemplateButton({ value, onApply, disabled, formValues, settings }: Props) {
  const s = settings || DEFAULT_OFFER_TEMPLATE_SETTINGS;
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [templates, setTemplates] = useState<OfferTemplate[]>([]);
  const [context, setContext] = useState<OfferTemplateContext>(EMPTY_OFFER_TEMPLATE_CONTEXT);
  const [selectedId, setSelectedId] = useState('');

  // Загрузка — ПО ОТКРЫТИЮ (снимок ссылки на заявку из значений формы)
  const load = async (rid: string) => {
    setLoading(true);
    setError('');
    try {
      const [list, ctx] = await Promise.all([
        loadActiveOfferTemplates(s),
        loadOfferTemplateContext(s, rid),
      ]);
      setTemplates(list);
      setContext(ctx);
      setSelectedId((prev) => (list.some((t) => t.id === prev) ? prev : (list[0]?.id || '')));
    } catch (e) {
      logErrorToJournal(e, 'OfferTemplateButton.load');
      setError(friendlyError(e));
      setTemplates([]);
      setContext(EMPTY_OFFER_TEMPLATE_CONTEXT);
    } finally {
      setLoading(false);
    }
  };

  const currentRequestId = () => String(formValues?.[s.requestField] ?? '');

  const openDialog = () => {
    setOpen(true);
    void load(currentRequestId());
  };

  const selected = templates.find((t) => t.id === selectedId) || null;
  const preview = selected ? applyOfferTemplate(selected.text, context.variables) : '';

  const apply = (mode: 'append' | 'replace') => {
    if (!selected) return;
    const text = applyOfferTemplate(selected.text, context.variables);
    const next = mode === 'replace' || value === ''
      ? text
      : (value.endsWith('\n') ? value + text : value + '\n' + text);
    onApply(next);
    setOpen(false);
  };

  return (
    <span className="relative inline-flex items-center">
      <button
        type="button"
        className="h-8 w-8 rounded-full flex items-center justify-center shrink-0 hover:bg-muted/40 disabled:opacity-40"
        title="Шаблон отклика"
        disabled={disabled}
        onClick={openDialog}
      >
        <FileText className="h-3.5 w-3.5" />
      </button>

      {open && (
        <UniversalWindow open onClose={() => setOpen(false)} title="Шаблон отклика" blocking={false} width="max-w-lg">
          <div className="p-4 space-y-2">
            <OfflineDataBanner sourceKey={`offer-templates:${s.catalog}`} />
            {context.requestId
              ? <div className="text-xs text-muted-foreground">{context.requestLabel || 'Заявка выбрана'} — переменные подставлены из неё.</div>
              : <div className="text-xs text-amber-600">Заявка не выбрана — переменные {'{{…}}'} подставятся пустыми. Сначала укажите заявку в форме.</div>}

            {loading && <div className="text-xs text-muted-foreground">Загрузка шаблонов…</div>}

            {!loading && error && (
              <div className="space-y-2">
                <div className="text-xs text-destructive break-words">{error}</div>
                <Button size="sm" variant="outline" onClick={() => void load(currentRequestId())}>Повторить</Button>
              </div>
            )}

            {!loading && !error && templates.length === 0 && (
              <div className="text-xs text-muted-foreground">
                Нет активных шаблонов. Их ведёт оператор в справочнике «Шаблоны откликов» (признак «Активен»).
              </div>
            )}

            {!loading && !error && templates.length > 0 && (
              <>
                <div className="space-y-1 max-h-40 overflow-auto pr-0.5">
                  {templates.map((t) => (
                    <button
                      key={t.id}
                      type="button"
                      className={`w-full text-left rounded border px-2.5 py-1.5 text-xs transition-colors ${t.id === selectedId ? 'border-primary bg-accent/40' : 'hover:bg-accent/20'}`}
                      onClick={() => setSelectedId(t.id)}
                    >
                      <div className="font-medium">{t.name || 'Без имени'}</div>
                      <div className="text-muted-foreground truncate">{t.text.replace(/\s+/g, ' ')}</div>
                    </button>
                  ))}
                </div>
                <div className="space-y-1">
                  <div className="text-[10px] font-medium text-muted-foreground uppercase">Предпросмотр</div>
                  <textarea
                    readOnly
                    value={preview}
                    className="w-full rounded-md border bg-input px-3 py-2 text-sm h-28 resize-none"
                  />
                  <div className="text-[10px] text-muted-foreground">{'{{Поле}}'} — реквизиты заявки; неизвестные поля становятся пустыми.</div>
                </div>
                <div className="flex justify-end gap-2 pt-1">
                  <Button size="sm" variant="outline" onClick={() => apply('replace')}>Заменить текст</Button>
                  <Button size="sm" onClick={() => apply('append')}>Вставить в текст</Button>
                </div>
              </>
            )}
          </div>
        </UniversalWindow>
      )}
    </span>
  );
}
